// components/ReasonSection.tsx
// 「安いだけ」じゃない。手もみ王が選ばれ続ける理由。セクションコンポーネント

import type React from "react";
import { SHOP_INFO } from "../config";

const ReasonSection: React.FC = () => {
  return (
    <section
      id="concept"
      className="pt-12 pb-24 md:pt-20 md:pb-32 bg-[#fffef9] relative overflow-hidden"
    >
      {/* Decorative Blobs */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-yellow-200 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob"></div>
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-orange-200 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-10 md:mb-20">
          <div className="inline-flex items-center justify-center p-1 rounded-full bg-gradient-to-r from-[var(--temomi-gold)] to-orange-400 mb-8 shadow-lg transform hover:scale-105 transition-transform duration-300">
            <div className="px-8 py-2 bg-white rounded-full">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--temomi-gold)] to-orange-400 font-black tracking-[0.2em] uppercase text-sm md:text-base">
                TEMOMIO QUALITY
              </span>
            </div>
          </div>

          <h2 className="text-3xl md:text-5xl font-black text-gray-900 leading-tight mb-4">
            <span className="inline-block">選ばれるには、</span>
            <span className="inline-block">ワケがある。</span>
          </h2>
          <p className="text-gray-600 text-base md:text-lg font-medium max-w-2xl mx-auto leading-relaxed">
            <span className="inline-block">「安い」だけじゃ満足できない。</span>
            <span className="inline-block">そんなあなたに贈る、</span>
            <span className="inline-block text-[var(--temomi-red)] font-bold underline decoration-wavy decoration-yellow-400 underline-offset-4">
              お値段以上の感動体験。
            </span>
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 lg:gap-10">
          {/* Reason 1 */}
          <div className="relative group">
            <div className="absolute inset-0 bg-white rounded-2xl md:rounded-[2rem] shadow-xl transform transition-transform duration-300 group-hover:-rotate-2"></div>
            <div className="relative bg-white rounded-2xl md:rounded-[2rem] p-5 md:p-6 border-2 border-gray-100 h-full flex flex-row md:flex-col items-center text-left md:text-center gap-4 md:gap-0 transition-transform duration-300 group-hover:-translate-y-2">
              <div className="absolute -top-1 left-1 md:top-4 md:left-6 text-5xl md:text-6xl font-black text-gray-200 -z-0 select-none font-serif italic">
                01
              </div>
              <div className="w-20 h-20 md:w-24 md:h-24 shrink-0 md:mb-4 relative z-10">
                <div className="absolute inset-0 bg-yellow-100 rounded-full animate-pulse"></div>
                <img
                  src={`${SHOP_INFO.basePath}/reason_training.webp`}
                  alt="技術力"
                  className="w-full h-full object-cover rounded-full border-4 border-white shadow-lg relative z-10"
                  width="200"
                  height="200"
                  loading="lazy"
                />
              </div>
              <div className="relative z-10">
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-1 md:mb-3">
                  <span className="block text-[10px] md:text-xs text-[var(--temomi-gold)] font-bold tracking-wider mb-0.5 md:mb-1">
                    SKILL
                  </span>
                  プロフェッショナルの
                  <br className="hidden md:block" />
                  確かな技術
                </h3>
                <p className="text-gray-600 text-xs md:text-sm leading-relaxed">
                  厳しい研修をクリアしたスタッフだけが在籍。誰が担当しても「当たり」の施術をお約束します。
                </p>
              </div>
            </div>
          </div>

          {/* Reason 2 */}
          <div className="relative group">
            <div className="absolute inset-0 bg-white rounded-2xl md:rounded-[2rem] shadow-xl transform transition-transform duration-300 group-hover:rotate-2"></div>
            <div className="relative bg-white rounded-2xl md:rounded-[2rem] p-5 md:p-6 border-2 border-gray-100 h-full flex flex-row md:flex-col items-center text-left md:text-center gap-4 md:gap-0 transition-transform duration-300 group-hover:-translate-y-2">
              <div className="absolute -top-1 left-1 md:top-4 md:left-6 text-5xl md:text-6xl font-black text-gray-200 -z-0 select-none font-serif italic">
                02
              </div>
              <div className="w-20 h-20 md:w-24 md:h-24 shrink-0 md:mb-4 relative z-10">
                <div className="absolute inset-0 bg-orange-100 rounded-full animate-pulse"></div>
                <img
                  src={`${SHOP_INFO.basePath}/reason_price.webp`}
                  alt="価格"
                  className="w-full h-full object-cover rounded-full border-4 border-white shadow-lg relative z-10"
                  width="200"
                  height="200"
                  loading="lazy"
                />
              </div>
              <div className="relative z-10">
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-1 md:mb-3">
                  <span className="block text-[10px] md:text-xs text-[var(--temomi-gold)] font-bold tracking-wide md:tracking-wider mb-0.5 md:mb-1">
                    PRICE
                  </span>
                  通い続けられる
                  <br className="hidden md:block" />
                  感動プライス
                </h3>
                <p className="text-gray-600 text-xs md:text-sm leading-relaxed">
                  「この技術でこの価格！？」
                  <br className="hidden md:block" />
                  驚きのコストパフォーマンスで、あなたの日常的なケアをサポートします。
                </p>
              </div>
            </div>
          </div>

          {/* Reason 3 */}
          <div className="relative group">
            <div className="absolute inset-0 bg-white rounded-2xl md:rounded-[2rem] shadow-xl transform transition-transform duration-300 group-hover:-rotate-2"></div>
            <div className="relative bg-white rounded-2xl md:rounded-[2rem] p-5 md:p-6 border-2 border-gray-100 h-full flex flex-row md:flex-col items-center text-left md:text-center gap-4 md:gap-0 transition-transform duration-300 group-hover:-translate-y-2">
              <div className="absolute -top-1 left-1 md:top-4 md:left-6 text-5xl md:text-6xl font-black text-gray-200 -z-0 select-none font-serif italic">
                03
              </div>
              <div className="w-20 h-20 md:w-24 md:h-24 shrink-0 md:mb-4 relative z-10">
                <div className="absolute inset-0 bg-red-100 rounded-full animate-pulse"></div>
                <img
                  src={`${SHOP_INFO.basePath}/reason_certified.webp`}
                  alt="安心"
                  className="w-full h-full object-cover rounded-full border-4 border-white shadow-lg relative z-10"
                  width="200"
                  height="200"
                  loading="lazy"
                />
              </div>
              <div className="relative z-10">
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-1 md:mb-3">
                  <span className="block text-[10px] md:text-xs text-[var(--temomi-gold)] font-bold tracking-wide md:tracking-wider mb-0.5 md:mb-1">
                    SAFETY
                  </span>
                  協会認定サロンの
                  <br className="hidden md:block" />
                  安心と信頼
                </h3>
                <p className="text-gray-600 text-xs md:text-sm leading-relaxed">
                  法令遵守・衛生管理も徹底。
                  <br className="hidden md:block" />
                  初めての方も、女性のお客様も、どうぞ安心してお任せください。
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReasonSection;
